import { useEffect, useId, useRef, type ReactNode } from "react";

import { CloseIcon } from "../icons";

export interface DrawerProps {
	readonly open: boolean;
	readonly title: string;
	/**
	 * Called whenever the drawer closes: the close button, Escape, a backdrop click (in browsers that support
	 * `closedby`) or `open` turning false. It can run after the parent already closed it, so make it idempotent.
	 */
	readonly onClose: () => void;
	readonly children: ReactNode;
	readonly footer?: ReactNode;
}

/**
 * Slide-in panel built on the native modal <dialog>: the browser traps focus, closes it on Escape, returns focus to the
 * opener and paints it in the top layer, so no portal or z-index is needed. Always slides in from the right edge
 * (DESIGN.md, "Drawer").
 */
export function Drawer({ open, title, onClose, children, footer }: DrawerProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const titleId = useId();

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return;
		}
		if (open && !dialog.open) {
			dialog.showModal();
		} else if (!open && dialog.open) {
			dialog.close();
		}
	}, [open]);

	return (
		<dialog
			ref={dialogRef}
			aria-labelledby={titleId}
			closedby="any"
			onClose={onClose}
			className="fixed inset-y-0 right-0 left-auto m-0 h-dvh max-h-none w-[min(520px,96vw)] max-w-none translate-x-0 border-0 border-l border-line bg-surface p-0 text-ink shadow-drawer transition-[translate] duration-[220ms] ease-ui backdrop:bg-[rgba(26,26,24,.25)] motion-reduce:transition-none starting:translate-x-full"
		>
			<div className="flex h-full flex-col">
				<div className="flex shrink-0 items-center gap-3 border-b border-line px-5 py-[13px]">
					<h2 id={titleId} className="flex-1 text-body font-semibold text-ink">
						{title}
					</h2>
					<button
						type="button"
						aria-label="Close"
						onClick={() => {
							dialogRef.current?.close();
						}}
						className="flex h-[26px] w-[26px] press cursor-pointer items-center justify-center rounded-ctl border border-line bg-transparent text-ink-3 hover:bg-bg"
					>
						<CloseIcon
							className="h-[11px] w-[11px] fill-none stroke-current stroke-[2]"
							strokeLinecap="round"
						/>
					</button>
				</div>
				<div className="thin-scroll flex-1 overflow-y-auto p-5">{children}</div>
				{footer === undefined ? null : (
					<div className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-3.5">
						{footer}
					</div>
				)}
			</div>
		</dialog>
	);
}
